import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  Image,
  Input,
  Link as ChakraLink,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import useAuth from '../../hooks/useAuth';
import { Form, Link, useNavigate } from 'react-router-dom';
import { Toast } from '../../components/Toast';
import { CiWarning } from 'react-icons/ci';
import { LoginForm, useLoginForm } from './hooks/loginForm';
import { AxiosError } from 'axios';

const GENERIC_LOGIN_ERROR = 'No pudimos cargar la sesión, por favor espere unos minutos y vuelva a intentarlo.';

const LOGIN_ERRORS_BY_STATUS: Partial<Record<number, string>> = {
  401: 'Correo o contraseña incorrectos.',
  403: 'Tu usuario no tiene acceso al portal.',
};

const getLoginErrorMessage = (error: unknown): string => {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  return (status && LOGIN_ERRORS_BY_STATUS[status]) || GENERIC_LOGIN_ERROR;
};

function Login() {
  const { login } = useAuth();
  const { controls, handleSubmit, errors } = useLoginForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleLogin = async (form: LoginForm) => {
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/home');
    } catch (error) {
      const message = getLoginErrorMessage(error);
      const ref = toast({
        position: 'bottom-right',
        render: () => (<Toast message={message} icon={<CiWarning />} onClose={() => toast.close(ref)} />),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      templateColumns={{ base: '1fr', md: '1fr 1fr' }} // 1 columna en pantallas pequeñas y 2 en medianas en adelante
      height="100vh"
    >
      {/* Sección izquierda con imagen */}
      <Box
        display={{ base: 'none', md: 'block' }}
        borderRadius="xl"
        overflow="hidden"
        height="90vh"
        margin="5vh 1em"
      >
        <Image
          src="/image-login.jpg" // Ruta de la imagen para 1x
          alt="Login Image"
          objectFit="cover"
          height="100%"
          width="100%"
        />
      </Box>

      {/* Sección derecha con el formulario */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
      >
        {
          loading ? <Spinner /> :
            <Box maxW="md" width="100%">
              <Image src="/logo.png" alt="Logo de la aplicación" marginBottom="xl" />

              <Box borderRadius={12} borderWidth={1} borderColor="neutral.400" padding="xxxl" bg="white">
                <Form onSubmit={handleSubmit(handleLogin)}>
                  <Stack spacing="xl">
                    <Heading as="h2" fontSize="xxl" marginBottom="l" color="neutral.900">Inicio de sesión</Heading>
                    <FormControl id="email" isInvalid={!!errors.email}>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="text"
                        placeholder="email@email.com"
                        borderColor={'neutral.400'}
                        color={'neutral.700'}
                        {...controls.email} />
                      <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl id="password" isInvalid={!!errors.password}>
                      <FormLabel>Contraseña</FormLabel>
                      <Input
                        type="password"
                        placeholder="****"
                        borderColor={'neutral.400'}
                        color={'neutral.700'}
                        {...controls.password} />
                      <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                    </FormControl>

                    <Flex justifyContent="space-between" flexDir="row" alignItems="center" marginTop="xl">
                      <ChakraLink as={Link} to="password-recovery" color="primary.800">Olvidé mi contraseña</ChakraLink>
                      <Button bgColor="primary.800" color="white" size="md" width="50" type="submit">
                        Ingresar
                      </Button>
                    </Flex>

                  </Stack>

                </Form>
              </Box>
              <Text mt={4} textAlign="center" hidden={true}>
                ¿No tienes una cuenta? <a href="/register">Regístrate</a>
              </Text>
            </Box>
        }
      </Box>
    </Grid>
  );
}

export default Login;
